import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useModuleVisibility } from '../modules/useModuleVisibility';
import { useAuth } from '../../context/AuthContext';
import { FeatureResult, HelpIndexEntry, searchFeatures } from '../modules/featureSearch';

/**
 * Header search over every page the signed-in user can open and every action they may take: type
 * "u" and Users, Device Users and the rest come up, type "create user" or "approve leave" for the
 * action; pick one (or press Enter for the first) to go there. Ctrl+K focuses the box. Buttons and
 * fields inside pages come from the help catalogue's index, loaded the first time the box is used.
 */
const FeatureSearch: React.FC = () => {
    const navigate = useNavigate();
    const { visibility, enabledModules } = useModuleVisibility();
    const { hasPermission } = useAuth();
    const [input, setInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const [controls, setControls] = useState<HelpIndexEntry[]>();
    const loading = useRef(false);

    const loadControls = () => {
        if (loading.current) return;
        loading.current = true;
        import('../modules/helpSearchIndex.json')
            .then(m => setControls((m.default ?? m) as HelpIndexEntry[]))
            .catch(() => { loading.current = false; });
    };

    const results = useMemo(
        () => searchFeatures(input, visibility, enabledModules, 8, hasPermission, controls),
        [input, visibility, enabledModules, hasPermission, controls],
    );

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const open = (result: FeatureResult) => {
        setInput('');
        inputRef.current?.blur();
        navigate(result.path, result.hint || result.targets
            ? { state: { searchPointer: { label: result.label, hint: result.hint, targets: result.targets } } }
            : undefined);
    };

    return (
        <Autocomplete<FeatureResult, false, false, false>
            options={results}
            filterOptions={x => x}
            getOptionLabel={o => o.label}
            isOptionEqualToValue={(a, b) => a.path === b.path && a.label === b.label}
            value={null}
            inputValue={input}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') { setInput(v); loadControls(); } }}
            onFocus={loadControls}
            onChange={(_, v) => { if (v) open(v); }}
            open={input.trim().length > 0}
            autoHighlight
            noOptionsText="No matching features"
            size="small"
            sx={{ flex: '1 1 160px', maxWidth: 360, mx: 2 }}
            renderOption={(props, o) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                return (
                    <Box component="li" key={`${o.label}|${o.path}`} {...rest} sx={{ display: 'flex', gap: 1 }}>
                        <span aria-hidden>{o.emoji ?? '•'}</span>
                        <span>{o.label}</span>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 1 }}>
                            {o.module}
                        </Typography>
                    </Box>
                );
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    inputRef={inputRef}
                    placeholder="Search features (Ctrl+K)"
                    inputProps={{ ...params.inputProps, 'aria-label': 'Search features' }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                />
            )}
        />
    );
};

export default FeatureSearch;
